import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpIcon } from '@heroicons/react/20/solid'
import cn from 'classnames'
import { useRouter } from 'next/router'
import { Fragment, useEffect, useState } from 'react'
import { SHOW_REVIEWS, THEME } from 'src/config/config'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'

interface SortbyOption {
  id: string
  label: string
}

const options: { [key: string]: SortbyOption[] } = {
  solutions: [
    { id: 'Default', label: 'Default' },
    { id: 'Alphabetically', label: 'Alphabetically' },
    ...(THEME === 'legaltech' && SHOW_REVIEWS ? [{ id: 'Rating', label: 'By Rating' }] : []),
  ],
  contents: [
    { id: 'Chronologically', label: 'Chronologically' },
    { id: 'Alphabetically', label: 'Alphabetically' },
  ],
  category_contents: [
    { id: 'Category_Chronologically', label: 'Default' },
    { id: 'Alphabetically', label: 'Alphabetically' },
  ],
}

export type SortByType = 'solutions' | 'contents' | 'category_contents'

interface SortbyDropdownProps {
  className?: string
  type: SortByType
}

export const SortbyDropdown: React.FC<SortbyDropdownProps> = ({ className = '', type }) => {
  const sortByOptions = options[type as string]
  const [selected, setSelected] = useState(sortByOptions[0])
  const router = useRouter()
  const { updateQueryParams } = useUpdateQueryParams()

  useEffect(() => {
    if (typeof router.query.sort === 'string') {
      const selectedOption = sortByOptions.find(({ id }) => id === router.query.sort)
      setSelected(selectedOption || sortByOptions[0])
    } else {
      setSelected(sortByOptions[0])
    }
  })

  const onChange = (value: SortbyOption) => {
    setSelected(value)
    updateQueryParams({
      sort: value.id,
      page: undefined,
    })
  }

  return (
    <div className={cn(className, 'top-16')}>
      <Listbox value={selected} onChange={onChange}>
        <div className="relative md:mt-1">
          <Listbox.Label></Listbox.Label>
          <Listbox.Button className="relative w-full py-2 pl-3 pr-10 border-mist border-solid border rounded-[20px] text-left min-h-[42px] bg-white">
            <span className="block truncate">
              <b>Sort by: </b>
              {selected && selected.label}
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <ChevronUpIcon className="w-5 h-5 text-gray-400" />
            </span>
          </Listbox.Button>
          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Listbox.Options className="absolute z-10 w-full py-1 mt-1 overflow-auto text-textdark bg-white rounded-[20px] shadow-lg">
              {sortByOptions.map((option, index) => (
                <Listbox.Option
                  key={index}
                  className={({ active }) =>
                    `${active ? 'bg-turquoise bg-opacity-10 text-textlight' : 'text-gray-900'}
                          cursor-default select-none relative py-2 pl-10 pr-4`
                  }
                  value={option}
                >
                  {({ selected, active }) => (
                    <>
                      <span className={`${selected ? 'font-medium' : 'font-normal'} block truncate`}>
                        {option.label}
                      </span>
                      {selected ? (
                        <span
                          className={`${active ? 'text-amber-600' : 'text-amber-600'}
                                absolute inset-y-0 left-0 flex items-center pl-3`}
                        >
                          <CheckIcon className="w-5 h-5 text-black" aria-hidden="true" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    </div>
  )
}
